import * as XLSX from 'xlsx';

export interface Employee {
  employeeCode: string;
  employeeName: string;
  workEmail: string;
  supervisorCode: string;
  supervisorName: string;
  position: string;
  department: string;
  departmentDesc: string;
  subDepartmentDesc: string;
  status: string;
}

let employeesCache: Employee[] | null = null;

export const loadEmployees = async (): Promise<Employee[]> => {
  if (employeesCache) {
    console.log('Using cached employee data:', employeesCache.length, 'employees');
    return employeesCache;
  }

  console.log('Loading employee data from XLSX...');
  try {
    const response = await fetch('/Org Report.xlsx');
    if (!response.ok) {
      throw new Error(`Failed to fetch XLSX: ${response.status} ${response.statusText}`);
    }
    const arrayBuffer = await response.arrayBuffer();
    console.log('XLSX loaded, size:', arrayBuffer.byteLength, 'bytes');

    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
    const firstSheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[firstSheetName];
    const rawData: any[] = XLSX.utils.sheet_to_json(worksheet);

    console.log('XLSX Headers:', Object.keys(rawData[0] || {}));
    console.log('First 3 rows of raw data:', rawData.slice(0, 3));

    const employees: Employee[] = rawData
      .map((row: any) => ({
        employeeCode: row.Employee_Code || '',
        employeeName: row.Employee_Name || '',
        workEmail: row.Work_Email || '',
        supervisorCode: row.Supervisor_Primary_Code || '',
        supervisorName: row.Supervisor_Primary || '',
        position: row.Position || '',
        department: row.Department || '',
        departmentDesc: row.Department_Desc || '',
        subDepartmentDesc: row.Sub_Department_Desc || '',
        status: row.Status || '',
      }))
      .filter((emp: Employee) => emp.employeeCode && emp.employeeName && emp.status === 'A'); // Only active employees

    employeesCache = employees;
    console.log('Successfully loaded', employees.length, 'active employees');

    // Show examples of parsed data
    const kinMaren = employees.find(e => e.employeeName && e.employeeName.includes('KING, MAREN'));
    const nahrwold = employees.find(e => e.employeeName && e.employeeName.includes('NAHRWOLD'));
    console.log('KING, MAREN data:', kinMaren);
    console.log('NAHRWOLD data:', nahrwold);

    return employees;
  } catch (error) {
    console.error('Error loading employees:', error);
    return [];
  }
};

export const getEmployeeByCode = (employees: Employee[], code: string): Employee | undefined => {
  return employees.find((emp) => emp.employeeCode === code);
};

export function getEmployeeDisplayLabel(employee: Employee): string {
  return `${employee.workEmail} - ${employee.employeeName.toLowerCase()}`;
};

export const getEmployeesByDepartment = (employees: Employee[], department: string): Employee[] => {
  return employees.filter((emp) => emp.department === department);
};

export const getEmployeesBySubDepartment = (employees: Employee[], subDepartment: string): Employee[] => {
  return employees.filter((emp) => emp.subDepartmentDesc === subDepartment);
};

export const isManagerInHierarchy = (
  employees: Employee[],
  requestorCode: string,
  managerCode: string
): boolean => {
  if (!requestorCode || !managerCode) {
    return false;
  }

  // If they're the same person, that's not valid
  if (requestorCode === managerCode) {
    return false;
  }

  const requestor = getEmployeeByCode(employees, requestorCode);
  if (!requestor) {
    return false;
  }

  // Traverse up the hierarchy from the requestor
  let currentCode = requestor.supervisorCode;
  const visited = new Set<string>(); // Prevent infinite loops

  while (currentCode && !visited.has(currentCode)) {
    visited.add(currentCode);

    // Check if we found the manager
    if (currentCode === managerCode) {
      return true;
    }

    // Move up one level
    const currentEmployee = getEmployeeByCode(employees, currentCode);
    if (!currentEmployee || !currentEmployee.supervisorCode) {
      // Reached the top of the hierarchy
      break;
    }

    currentCode = currentEmployee.supervisorCode;
  }

  return false;
};

export const getManagerChain = (employees: Employee[], employeeCode: string): Employee[] => {
  const chain: Employee[] = [];
  const employee = getEmployeeByCode(employees, employeeCode);
  
  if (!employee) {
    return chain;
  }

  let currentCode = employee.supervisorCode;
  const visited = new Set<string>();

  while (currentCode && !visited.has(currentCode)) {
    visited.add(currentCode);
    const manager = getEmployeeByCode(employees, currentCode);
    
    if (!manager) {
      break;
    }

    chain.push(manager);
    currentCode = manager.supervisorCode;
  }

  return chain;
};
